import { useCallback, useEffect, useRef, useState } from "react";
import { getRecommendations } from "@/api/customer.api.js";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll.js";

const PAGE_SIZE = 8;

export function useProductRecommendations({
    excludeCart = false,
    excludeProductId = null,
    enabled = true,
    resetKey = ""
}) {
    const [products, setProducts] = useState([]);
    const [seed, setSeed] = useState(null);
    const [offset, setOffset] = useState(0);
    const [hasNext, setHasNext] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState("");
    const seedRef = useRef(null);
    const offsetRef = useRef(0);

    const fetchPage = useCallback(async (nextOffset, nextSeed, append) => {
        if (!enabled) {
            return;
        }
        if (append) {
            setLoadingMore(true);
        } else {
            setLoading(true);
        }
        setError("");
        try {
            const response = await getRecommendations({
                offset: nextOffset,
                limit: PAGE_SIZE,
                seed: nextSeed || undefined,
                excludeCart,
                excludeProductId: excludeProductId || undefined
            });
            const pageItems = response.data || [];
            setProducts((prev) => (append ? [...prev, ...pageItems] : pageItems));
            setHasNext(!!response.hasNext);
            const resolvedSeed = response.seed || nextSeed;
            seedRef.current = resolvedSeed;
            setSeed(resolvedSeed);
            offsetRef.current = nextOffset;
            setOffset(nextOffset);
        } catch (err) {
            setError(err.message);
            if (!append) {
                setProducts([]);
                setHasNext(false);
            }
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [enabled, excludeCart, excludeProductId]);

    useEffect(() => {
        if (!enabled) {
            setProducts([]);
            setHasNext(false);
            setError("");
            return;
        }
        seedRef.current = null;
        offsetRef.current = 0;
        setSeed(null);
        setOffset(0);
        fetchPage(0, null, false);
    }, [enabled, excludeCart, excludeProductId, resetKey, fetchPage]);

    const loadMore = useCallback(() => {
        const nextOffset = offsetRef.current + PAGE_SIZE;
        fetchPage(nextOffset, seedRef.current, true);
    }, [fetchPage]);

    const sentinelRef = useInfiniteScroll({ hasNext, loading, loadingMore, onLoadMore: loadMore });

    return {
        products,
        loading,
        loadingMore,
        error,
        sentinelRef,
        hasProducts: products.length > 0
    };
}
